import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Chip,
  IconButton,
  Grid,
  CircularProgress,
  Badge
} from '@mui/material';
import {
  Forum,
  Send,
  Close,
  SmartToy,
  SupportAgent,
  Person,
  Done,
  FlashOn
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';
import { io, Socket } from 'socket.io-client';

interface SupportMessage {
  id: string;
  senderType: 'USER' | 'AI' | 'ADMIN';
  senderName: string;
  content: string;
  createdAt: string;
}

interface SupportSession {
  id: string;
  userId?: string;
  guestName?: string;
  guestEmail?: string;
  guestIp?: string;
  status: 'ACTIVE_AI' | 'ACTIVE_HUMAN' | 'CLOSED';
  needsHuman: boolean;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  messages: SupportMessage[];
  unreadCount?: number;
}

export const SupportCenterPage: React.FC = () => {
  const [sessions, setSessions] = useState<SupportSession[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(false);

  const socketRef = useRef<Socket | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const BACKEND_URL = process.env.NODE_ENV === 'production'
    ? 'https://rideclubnet.com'
    : (process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:3001');

  const API_URL = process.env.NODE_ENV === 'production'
    ? 'https://rideclubnet.com/api'
    : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

  const selectedSession = sessions.find(s => s.id === selectedSessionId) || null;

  // Canned Responses
  const cannedResponses = [
    { label: '0% Comm Commission Policy', text: 'Ride Club takes 0% commission on passenger fares. Drivers receive 100% of the fare, and payouts are transferred directly to their Stripe Connect Express account 3 business days after ride completion.' },
    { label: '$1/day Posting Fee', text: 'Drivers pay a flat fee of $1/day to create and post unlimited rides on the Ride Club platform. Credits are purchased via prepaid credit packages in your profile (e.g. $10 package gives 10 days of posting credits).' },
    { label: 'Refund/Dispute Policy', text: 'Passengers can cancel bookings according to our cancellation rules. If a driver cancels, passengers get an automatic full refund. Passengers can file a dispute within 3 business days of the departed ride to request refund/escrow freeze if the ride did not occur.' },
    { label: 'Driver License Verification', text: 'To become a verified driver, go to your profile, click "Become Driver", upload your driver\'s license and vehicle details, and our verification team will review your application within 24-48 hours.' }
  ];

  // Fetch all active sessions
  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_URL}/admin/support/sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setSessions(response.data.sessions || []);
      }
    } catch (err) {
      console.error('Failed to fetch support sessions:', err);
    } finally {
      setLoading(false);
    }
  };

  // Socket connection for real-time takeover updates
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) return;

    console.log('🔌 Connecting Admin Support Socket.io...');
    const socket = io(BACKEND_URL, {
      auth: { token },
      transports: ['websocket', 'polling']
    });

    socket.on('connect', () => {
      console.log('✅ Admin support socket connected:', socket.id);
    });

    // Listen for new human escalations
    socket.on('support:new_ticket', (ticket: any) => {
      console.log('🚨 New support escalation ticket received:', ticket);
      fetchSessions();
    });

    // Listen for incoming messages on active chats
    socket.on('support:message:receive', (data: any) => {
      console.log('📩 Socket message received:', data);
      const { sessionId, message } = data;
      
      setSessions(prev => prev.map(s => {
        if (s.id === sessionId) {
          // Avoid duplicates
          if (s.messages.some(m => m.id === message.id)) return s;
          
          const updatedMessages = [...s.messages, message];
          const isFromCurrentUser = message.senderType === 'ADMIN';
          const unreadIncrement = !isFromCurrentUser && selectedSessionId !== sessionId ? 1 : 0;

          return {
            ...s,
            messages: updatedMessages,
            status: message.senderType === 'ADMIN' ? 'ACTIVE_HUMAN' as const : s.status,
            unreadCount: (s.unreadCount || 0) + unreadIncrement
          };
        }
        return s;
      }));
    });

    // Listen for closed sessions
    socket.on('support:session:closed', (data: any) => {
      console.log('🔒 Socket session closed:', data);
      const { sessionId } = data;
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      if (selectedSessionId === sessionId) {
        setSelectedSessionId(null);
      }
    });

    socketRef.current = socket;

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [selectedSessionId]);

  // Scroll to bottom of chat history when active chat updates
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedSession?.messages]);

  // Clear unread count when session is selected
  useEffect(() => {
    if (selectedSessionId) {
      setSessions(prev => prev.map(s => s.id === selectedSessionId ? { ...s, unreadCount: 0 } : s));
    }
  }, [selectedSessionId]);

  const handleSelectSession = (id: string) => {
    setSelectedSessionId(id);
  };

  const handleSendReply = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!inputText.trim() || !selectedSessionId) return;

    const currentInput = inputText.trim();
    setInputText('');

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(
        `${API_URL}/admin/support/sessions/${selectedSessionId}/reply`,
        { content: currentInput },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const adminMsg = response.data.message;
        setSessions(prev => prev.map(s => {
          if (s.id === selectedSessionId) {
            // Avoid duplicates
            if (s.messages.some(m => m.id === adminMsg.id)) return s;
            return {
              ...s,
              status: 'ACTIVE_HUMAN',
              messages: [...s.messages, adminMsg]
            };
          }
          return s;
        }));
      }
    } catch (err) {
      console.error('Failed to send admin reply:', err);
    }
  };

  const handleCloseSession = async () => {
    if (!selectedSessionId) return;

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(
        `${API_URL}/admin/support/sessions/${selectedSessionId}/close`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSessions(prev => prev.filter(s => s.id !== selectedSessionId));
        setSelectedSessionId(null);
      }
    } catch (err) {
      console.error('Failed to close support session:', err);
    }
  };

  const handleInsertCannedResponse = (text: string) => {
    setInputText(prev => prev + (prev ? ' ' : '') + text);
  };

  const getSessionName = (session: SupportSession) => {
    if (session.user) {
      return `${session.user.firstName} ${session.user.lastName}`;
    }
    return session.guestName || 'Guest Visitor';
  };

  const getSessionEmail = (session: SupportSession) => {
    if (session.user) {
      return session.user.email;
    }
    return session.guestEmail || 'N/A';
  };

  return (
    <Box sx={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h4" fontWeight="bold" sx={{ mb: 2 }}>
        Live Support Center
      </Typography>

      <Grid container spacing={3} sx={{ flexGrow: 1, height: '90%', overflow: 'hidden' }}>
        {/* Left Side: Sessions List */}
        <Grid size={{ xs: 12, md: 4 }} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Paper sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', borderRadius: '16px', overflow: 'hidden' }}>
            <Box sx={{ p: 2, bgcolor: '#1E4620', color: 'white' }}>
              <Typography variant="h6" fontWeight="bold">
                Active Live Sessions
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Manage ongoing support and human escallations
              </Typography>
            </Box>

            <Divider />

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
                <CircularProgress color="primary" />
              </Box>
            ) : sessions.length === 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flexGrow: 1, p: 3, textAlign: 'center' }}>
                <Forum sx={{ fontSize: 48, color: 'text.secondary', mb: 1.5, opacity: 0.5 }} />
                <Typography color="textSecondary" variant="body2">
                  No active support requests right now.
                </Typography>
              </Box>
            ) : (
              <List sx={{ flexGrow: 1, overflowY: 'auto', p: 0 }}>
                {sessions.map((session) => {
                  const isSelected = session.id === selectedSessionId;
                  const lastMessage = session.messages[session.messages.length - 1];
                  const hasUnread = (session.unreadCount || 0) > 0;
                  const displayName = getSessionName(session);

                  return (
                    <ListItem key={session.id} disablePadding divider>
                      <ListItemButton
                        selected={isSelected}
                        onClick={() => handleSelectSession(session.id)}
                        sx={{
                          py: 1.5,
                          '&.Mui-selected': {
                            bgcolor: 'rgba(46, 125, 50, 0.08)',
                            '&:hover': {
                              bgcolor: 'rgba(46, 125, 50, 0.12)'
                            }
                          }
                        }}
                      >
                        <ListItemAvatar>
                          <Badge color="error" badgeContent={session.unreadCount || 0} invisible={!hasUnread}>
                            <Avatar sx={{ bgcolor: session.user ? '#2E7D32' : '#78909c' }}>
                              {displayName[0]}
                            </Avatar>
                          </Badge>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" fontWeight={hasUnread ? 'bold' : 'medium'} noWrap sx={{ maxWidth: '65%' }}>
                                {displayName}
                              </Typography>
                              <Chip
                                label={session.status === 'ACTIVE_AI' ? 'AI' : 'Human'}
                                size="small"
                                color={session.status === 'ACTIVE_AI' ? 'default' : 'primary'}
                                variant={session.status === 'ACTIVE_AI' ? 'outlined' : 'filled'}
                                sx={{ height: 18, fontSize: '0.65rem' }}
                              />
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                display="block"
                                sx={{
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  fontWeight: hasUnread ? 'bold' : 'normal'
                                }}
                              >
                                {lastMessage ? lastMessage.content : 'No messages yet'}
                              </Typography>
                              {session.needsHuman && session.status === 'ACTIVE_AI' && (
                                <Chip
                                  label="Escalating..."
                                  size="small"
                                  color="error"
                                  sx={{ height: 16, fontSize: '0.6rem', mt: 0.5 }}
                                />
                              )}
                            </Box>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Right Side: Conversation Area */}
        <Grid size={{ xs: 12, md: 8 }} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {selectedSession ? (
            <Paper sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', borderRadius: '16px', overflow: 'hidden', height: '100%' }}>
              {/* Header */}
              <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#F8FAFC' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: selectedSession.user ? '#2E7D32' : '#78909c' }}>
                    {getSessionName(selectedSession)[0]}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {getSessionName(selectedSession)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {getSessionEmail(selectedSession)} • {selectedSession.user ? 'Logged-In' : 'Guest'}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  {selectedSession.status === 'ACTIVE_AI' && (
                    <Button
                      variant="contained"
                      color="warning"
                      startIcon={<SupportAgent />}
                      size="small"
                      onClick={() => {
                        setInputText("I am taking over this chat as a live Ride Club agent. How can I help you?");
                        setTimeout(() => handleSendReply(), 50);
                      }}
                    >
                      Takeover Live
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    color="success"
                    startIcon={<Done />}
                    size="small"
                    onClick={handleCloseSession}
                  >
                    Resolve & Close
                  </Button>
                </Box>
              </Box>

              {/* Message History */}
              <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3, bgcolor: '#F8FAFC', display: 'flex', flexDirection: 'column', gap: 2 }}>
                {selectedSession.messages.map((msg, index) => {
                  const isUser = msg.senderType === 'USER';
                  const isAi = msg.senderType === 'AI';
                  const isSystem = msg.senderName === 'System';

                  if (isSystem) {
                    return (
                      <Box key={msg.id || index} sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
                        <Paper elevation={0} sx={{ px: 2, py: 0.5, borderRadius: '8px', bgcolor: '#e2e8f0' }}>
                          <Typography variant="caption" color="text.secondary">
                            {msg.content}
                          </Typography>
                        </Paper>
                      </Box>
                    );
                  }

                  return (
                    <Box
                      key={msg.id || index}
                      sx={{
                        display: 'flex',
                        justifyContent: isUser ? 'flex-start' : 'flex-end',
                        gap: 1.5
                      }}
                    >
                      {isUser && (
                        <Avatar sx={{ width: 32, height: 32, bgcolor: '#cfd8dc' }}>
                          <Person />
                        </Avatar>
                      )}
                      
                      <Box sx={{ maxWidth: '75%' }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            ml: isUser ? 1 : 0,
                            mr: isUser ? 0 : 1,
                            display: 'block',
                            textAlign: isUser ? 'left' : 'right'
                          }}
                        >
                          {msg.senderName} {isAi && '[BOT]'}
                        </Typography>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 1.5,
                            borderRadius: isUser ? '16px 16px 16px 4px' : '16px 16px 4px 16px',
                            bgcolor: isUser ? 'white' : isAi ? '#E3F2FD' : '#E8F5E9',
                            color: 'text.primary',
                            border: '1px solid',
                            borderColor: isUser ? '#E2E8F0' : isAi ? '#BBDEFB' : '#C8E6C9',
                            '& p': { m: 0, '&:not(:last-child)': { mb: 1 } },
                            '& ul, & ol': { m: 0, pl: 2 }
                          }}
                        >
                          <ReactMarkdown>{msg.content}</ReactMarkdown>
                        </Paper>
                      </Box>

                      {!isUser && (
                        <Avatar sx={{ width: 32, height: 32, bgcolor: isAi ? '#1976D2' : '#2E7D32' }}>
                          {isAi ? <SmartToy fontSize="small" /> : <SupportAgent fontSize="small" />}
                        </Avatar>
                      )}
                    </Box>
                  );
                })}
                <div ref={messagesEndRef} />
              </Box>

              {/* Quick Canned Responses */}
              <Box sx={{ p: 1.5, bgcolor: '#ffffff', borderTop: '1px solid #e0e0e0', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary" sx={{ width: '100%', mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <FlashOn sx={{ fontSize: 14 }} /> Canned Responses:
                </Typography>
                {cannedResponses.map((cr, idx) => (
                  <Chip
                    key={idx}
                    label={cr.label}
                    size="small"
                    color="primary"
                    variant="outlined"
                    onClick={() => handleInsertCannedResponse(cr.text)}
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.04)' } }}
                  />
                ))}
              </Box>

              {/* Input Area */}
              <Box component="form" onSubmit={handleSendReply} sx={{ p: 2, borderTop: '1px solid #e0e0e0', display: 'flex', gap: 2, bgcolor: '#ffffff' }}>
                <TextField
                  placeholder={selectedSession.status === 'CLOSED' ? 'Session is resolved and closed.' : 'Type your message to user...'}
                  disabled={selectedSession.status === 'CLOSED'}
                  size="small"
                  value={inputText}
                  onChange={e => setInputText(e.target.value)}
                  fullWidth
                  variant="outlined"
                  autoComplete="off"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    }
                  }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={!inputText.trim() || selectedSession.status === 'CLOSED'}
                  endIcon={<Send />}
                  sx={{ px: 3, fontWeight: 'bold' }}
                >
                  Send
                </Button>
              </Box>
            </Paper>
          ) : (
            <Paper sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', borderRadius: '16px', border: '1px dashed #cbd5e1' }}>
              <Forum sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.4 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Session Selected
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300, textAlign: 'center' }}>
                Select an active chat session from the left sidebar to takeover conversation or reply.
              </Typography>
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default SupportCenterPage;
